import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useConfirmAnonymousPayment from '../../../hooks/query/useConfirmAnonymousPayment'

type PaymentStatus = 'loading' | 'success' | 'error'

export const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate()
  const [paymentAmount, setPaymentAmount] = useState(0)
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('loading')
  const [errorMessage, setErrorMessage] = useState('')

  const { mutate: confirmPayment, isPending } = useConfirmAnonymousPayment()

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const amount = urlParams.get('amount')
    const paymentKey = urlParams.get('paymentKey')
    const orderId = urlParams.get('orderId')
    const email = urlParams.get('email')

    if (!amount || !paymentKey || !orderId) {
      alert('잘못된 접근입니다.')
      navigate('/')
      return
    }

    setPaymentAmount(Number(amount))

    // 익명 결제 승인 API 호출
    confirmPayment(
      {
        paymentKey,
        orderId,
        amount: Number(amount),
        email: email || '',
      },
      {
        onSuccess: () => {
          setPaymentStatus('success')
        },
        onError: (error: any) => {
          setPaymentStatus('error')
          setErrorMessage(
            error?.response?.data?.message ||
              '결제 승인 중 오류가 발생했습니다.',
          )
        },
      },
    )
  }, [navigate, confirmPayment])

  const handleGoHome = () => {
    navigate('/')
  }

  const handleRetry = () => {
    window.location.reload()
  }

  // 로딩 상태
  if (paymentStatus === 'loading' || isPending) {
    return (
      <div className="mx-auto max-w-md p-6">
        <div className="text-center">
          <div className="mb-6">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
          </div>

          <h1 className="mb-6 text-2xl font-bold text-gray-700">
            결제 승인 처리 중...
          </h1>

          <div className="mb-6 rounded-lg bg-gray-100 p-6">
            <p className="mb-2 text-lg font-semibold">결제 금액</p>
            <p className="text-2xl font-bold text-blue-600">
              {paymentAmount.toLocaleString()}원
            </p>
          </div>

          <div className="mb-8 rounded-lg bg-blue-50 p-4">
            <p className="text-blue-800">
              결제 승인을 처리하고 있습니다. 잠시만 기다려주세요.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // 성공 상태
  if (paymentStatus === 'success') {
    return (
      <div className="mx-auto max-w-md p-6">
        <div className="text-center">
          <div className="mb-6">
            <span className="text-6xl">✅</span>
          </div>

          <h1 className="mb-6 text-3xl font-bold text-green-600">결제 완료</h1>

          <div className="mb-6 rounded-lg bg-gray-100 p-6">
            <p className="mb-2 text-lg font-semibold">결제 금액</p>
            <p className="text-2xl font-bold text-blue-600">
              {paymentAmount.toLocaleString()}원
            </p>
          </div>

          <div className="mb-8 rounded-lg bg-green-50 p-4">
            <p className="text-green-800">결제가 성공적으로 완료되었습니다.</p>
          </div>

          <button
            onClick={handleGoHome}
            className="w-full rounded-lg bg-blue-600 py-4 text-lg font-bold text-white hover:bg-blue-700"
          >
            홈으로 돌아가기
          </button>
        </div>
      </div>
    )
  }

  // 실패 상태
  return (
    <div className="mx-auto max-w-md p-6">
      <div className="text-center">
        <div className="mb-6">
          <span className="text-6xl">❌</span>
        </div>

        <h1 className="mb-6 text-3xl font-bold text-red-600">결제 실패</h1>

        <div className="mb-6 rounded-lg bg-gray-100 p-6">
          <p className="mb-2 text-lg font-semibold">결제 금액</p>
          <p className="text-2xl font-bold text-blue-600">
            {paymentAmount.toLocaleString()}원
          </p>
        </div>

        <div className="mb-8 rounded-lg bg-red-50 p-4">
          <p className="mb-2 font-semibold text-red-800">결제 승인 실패</p>
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleRetry}
            className="flex-1 rounded-lg bg-orange-600 py-4 text-lg font-bold text-white hover:bg-orange-700"
          >
            재시도
          </button>
          <button
            onClick={handleGoHome}
            className="flex-1 rounded-lg bg-gray-600 py-4 text-lg font-bold text-white hover:bg-gray-700"
          >
            홈으로
          </button>
        </div>
      </div>
    </div>
  )
}
